import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companiesApi, coursesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Crown, ArrowLeft, BookOpen, MagnifyingGlass, GearSix, Clock, Users as UsersIcon,
  Chalkboard, Plus, PencilSimple, Trash, WarningCircle, SealCheck
} from '@phosphor-icons/react';

const TRAINING_TYPES = [
  'Certificación o Calificación',
  'Legal',
  'SSO Específica Área',
  'SSO General Empresa'
];

const MODALITIES = [
  'E-Learning',
  'E-Learning Teórico-Práctico',
  'Presencial',
  'Presencial Teórico-Práctico',
  'Teórico-Práctico',
  'Video'
];

const emptyCourse = {
  name: '',
  description: '',
  training_type: 'SSO Específica Área',
  modality: 'Presencial',
  max_participants: '',
  default_duration_minutes: 60,
  default_passing_percentage: 70,
  validity_years: '',
};

export default function MandanteDetailPage() {
  const { mandanteId } = useParams();
  const navigate = useNavigate();
  const [mandante, setMandante] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({ ...emptyCourse });

  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [mandanteRes, coursesRes] = await Promise.all([
        companiesApi.getById(mandanteId),
        coursesApi.getAll({ client_id: mandanteId })
      ]);
      setMandante(mandanteRes.data);
      setCourses(coursesRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, [mandanteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingCourse(null);
    setFormData({ ...emptyCourse });
    setDialogOpen(true);
  };

  const openEdit = (course, e) => {
    e.stopPropagation();
    setEditingCourse(course);
    setFormData({
      name: course.name,
      description: course.description || '',
      training_type: course.training_type || 'SSO Específica Área',
      modality: course.modality || 'Presencial',
      max_participants: course.max_participants || '',
      default_duration_minutes: course.default_duration_minutes || 60,
      default_passing_percentage: course.default_passing_percentage || 70,
      validity_years: course.validity_years || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      ...formData,
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
      default_duration_minutes: parseInt(formData.default_duration_minutes) || 60,
      default_passing_percentage: parseFloat(formData.default_passing_percentage) || 70,
      validity_years: formData.validity_years ? parseInt(formData.validity_years) : null,
      client_id: mandanteId,
    };
    try {
      if (editingCourse) {
        await coursesApi.update(editingCourse.id, payload);
        toast.success('Curso actualizado');
      } else {
        await coursesApi.create(payload);
        toast.success('Curso creado');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await coursesApi.delete(deleteDialog.id);
      toast.success('Curso eliminado');
      setDeleteDialog(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
    finally { setDeleting(false); }
  };

  const filtered = courses.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;
  if (!mandante) return <div className="text-center py-12 text-[#2B4B8C]/40">Mandante no encontrado</div>;

  return (
    <div className="space-y-6" data-testid="mandante-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/mandantes')} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <Crown size={24} weight="fill" className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#2B4B8C]">{mandante.name}</h1>
            <div className="flex items-center gap-3 text-xs text-[#2B4B8C]/50 mt-0.5">
              {mandante.trade_name && <span>{mandante.trade_name}</span>}
              {mandante.rut && <span className="font-mono">{mandante.rut}</span>}
              {mandante.email && <span>{mandante.email}</span>}
            </div>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-[#2BBAA4] hover:bg-[#2BBAA4]/90 text-white" data-testid="add-course-btn">
          <Plus size={16} className="mr-2" /> Nuevo Curso
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><BookOpen size={18} className="text-blue-600" /></div>
            <div><p className="text-2xl font-bold text-[#2B4B8C]">{courses.length}</p><p className="text-xs text-[#2B4B8C]/50">Cursos</p></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#2BBAA4]/10 flex items-center justify-center"><Chalkboard size={18} className="text-[#2BBAA4]" /></div>
            <div><p className="text-2xl font-bold text-[#2B4B8C]">{courses.reduce((s, c) => s + (c.trainings_count || 0), 0)}</p><p className="text-xs text-[#2B4B8C]/50">Capacitaciones</p></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Crown size={18} className="text-amber-600" /></div>
            <div><p className="text-2xl font-bold text-[#2B4B8C]">{mandante.workers_count || 0}</p><p className="text-xs text-[#2B4B8C]/50">Trabajadores</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      {courses.length > 5 && (
        <div className="relative max-w-sm">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/40" />
          <Input placeholder="Buscar curso..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="course-search" />
        </div>
      )}

      {/* Course list */}
      <div className="space-y-2">
        {filtered.map((course) => (
          <Card key={course.id} className="bg-white border-[#2B4B8C]/10 hover:border-[#2BBAA4]/30 transition-all duration-150 group" data-testid={`course-row-${course.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <BookOpen size={16} className="text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-[#2B4B8C] text-sm">{course.name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[#2B4B8C]/40 flex-wrap">
                      {course.training_type && <span className="px-1.5 py-0.5 rounded bg-[#2B4B8C]/5">{course.training_type}</span>}
                      {course.modality && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{course.modality}</span>}
                      <span className="flex items-center gap-1"><Clock size={12} /> {course.default_duration_minutes} min</span>
                      {course.max_participants && <span className="flex items-center gap-1"><UsersIcon size={12} /> {course.max_participants} pers.</span>}
                      <span className="flex items-center gap-1"><Chalkboard size={12} /> {course.trainings_count || 0} capacitaciones</span>
                      {course.validity_years ? (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#2BBAA4]/10 text-[#2BBAA4] font-medium" data-testid={`course-validity-badge-${course.id}`}>
                          <SealCheck size={12} weight="fill" /> Vigencia: {course.validity_years} año{course.validity_years > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#2B4B8C]/5 text-[#2B4B8C]/40" data-testid={`course-validity-badge-${course.id}`}>
                          <SealCheck size={12} /> Sin vigencia
                        </span>
                      )}
                    </div>
                    {course.description && <p className="text-xs text-[#2B4B8C]/30 mt-1 truncate">{course.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={(e) => openEdit(course, e)} className="p-2 rounded-md hover:bg-[#2B4B8C]/5 text-[#2B4B8C]/30 hover:text-[#2B4B8C] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-course-${course.id}`}>
                    <PencilSimple size={15} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setDeleteDialog(course); }} className="p-2 rounded-md hover:bg-red-50 text-[#2B4B8C]/30 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-course-${course.id}`}>
                    <Trash size={15} />
                  </button>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/admin/courses/${course.id}/config`)} className="border-[#2BBAA4]/30 text-[#2BBAA4] hover:bg-[#2BBAA4]/5 h-8 text-xs ml-2" data-testid={`configure-course-${course.id}`}>
                    <GearSix size={14} className="mr-1.5" /> Configurar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {courses.length === 0 && (
        <div className="text-center py-12 text-[#2B4B8C]/40">
          <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
          <p>Este mandante no tiene cursos configurados</p>
          <Button onClick={openCreate} variant="outline" size="sm" className="mt-3"><Plus size={14} className="mr-1.5" /> Agregar primer curso</Button>
        </div>
      )}

      {/* Create/Edit Course Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">{editingCourse ? 'Editar Curso' : 'Nuevo Curso'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Nombre del curso *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" placeholder="Ej: Inducción Hombre Nuevo" data-testid="course-name-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Tipo Capacitación *</Label>
                <Select value={formData.training_type} onValueChange={(v) => setFormData({ ...formData, training_type: v })}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="course-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAINING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Modalidad *</Label>
                <Select value={formData.modality} onValueChange={(v) => setFormData({ ...formData, modality: v })}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="course-modality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODALITIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Observación</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C] min-h-[70px]" placeholder="Observación o descripción del curso..." data-testid="course-observation" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Capacidad (personas)</Label>
                <Input type="number" value={formData.max_participants} onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" placeholder="Ej: 80" data-testid="course-capacity" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Duración (minutos)</Label>
                <Input type="number" value={formData.default_duration_minutes} onChange={(e) => setFormData({ ...formData, default_duration_minutes: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" placeholder="Ej: 60" data-testid="course-duration" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Años de vigencia del certificado</Label>
              <Input type="number" min="0" value={formData.validity_years} onChange={(e) => setFormData({ ...formData, validity_years: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" placeholder="Ej: 2 (déjelo vacío si no vence)" data-testid="course-validity-years" />
              <p className="text-xs text-[#2B4B8C]/50">Define la vigencia del certificado según el tipo de curso. El vencimiento se calcula desde la fecha de la capacitación.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#2B4B8C]/10">Cerrar</Button>
            <Button onClick={handleSave} disabled={!formData.name.trim()} className="bg-[#2BBAA4] hover:bg-[#2BBAA4]/90 text-white" data-testid="save-course-btn">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><WarningCircle size={22} weight="fill" /> Eliminar Curso</DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-[#2B4B8C]/70">Se eliminará <strong className="text-[#2B4B8C]">{deleteDialog?.name}</strong> y sus capacitaciones vinculadas.</p>
            <p className="text-xs text-red-500 font-medium mt-2">Esta acción es irreversible.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-[#2B4B8C]/10">Cancelar</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-course">{deleting ? 'Eliminando...' : 'Eliminar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
